import { useEffect, useRef, useState } from "react";
import { X, Maximize2, RefreshCw, Eye } from "lucide-react";

/**
 * FloatingMiniPreview — schwebendes 360x540-Vorschau-Fenster, das beim Edit
 * automatisch zur jeweiligen Section scrollt.
 *
 * Mechanik:
 * - User klickt auf eine Section im Editor (TOC oder klappt eine auf)
 * - Wir lauschen auf das Custom-Event "editor-section-open"
 * - Wir mappen den Section-Key (z.B. "hero") auf den DOM-Anchor in der
 *   gerenderten Demo (z.B. "#services" für Services, "#about" für About,
 *   "#contact" für Contact)
 * - Wir laden den Iframe mit `src=…&mp=anchor` und der Iframe-Side scrollt
 *   automatisch zu diesem Anchor (siehe inject_anchor_scroll im Backend)
 *
 * Props:
 *   previewBase: string — URL des aktuellen Preview-Iframes (ohne Hash)
 *   refreshKey: number — bei Änderung wird der Iframe neu geladen
 *   onClose: () => void
 */
const SECTION_TO_ANCHOR = {
  theme: "#",          // Theme = oben (kein Anchor)
  media: "#",
  hero: "#",
  stats: "#stats",
  about: "#about",
  services: "#services",
  trust: "#testimonials",
  team: "#team",
  industry: "#",
  contact: "#contact",
  "custom-pages": "#",
  impressum: "#",
  datenschutz: "#",
};

// SECTION_TARGETS: mappt eine Editor-Section-ID auf ein Vorschau-Ziel.
// - { anchor: "#xyz" }       → bleibt auf der Index-Page und scrollt zum Anchor
// - { page: "impressum" }    → wechselt die Sub-Page im Iframe (anchor optional)
// - { anchor: "#" }          → Index-Page, kein spezifischer Scroll (= ganz nach oben)
// Wird vom `editor-section-open`-Listener konsumiert.
const SECTION_TARGETS = {
  theme:         { anchor: "#" },
  media:         { anchor: "#" },
  hero:          { anchor: "#" },
  stats:         { anchor: "#stats" },
  about:         { anchor: "#about" },
  services:      { anchor: "#services" },
  trust:         { anchor: "#testimonials" },
  team:          { anchor: "#team" },
  industry:      { anchor: "#" },
  contact:       { anchor: "#contact" },
  "custom-pages": { anchor: "#" },
  "subpages-ai": { anchor: "#" },
  impressum:     { page: "impressum", anchor: "#" },
  datenschutz:   { page: "datenschutz", anchor: "#" },
};

export const FloatingMiniPreview = ({ previewBase, refreshKey, onClose, size: sizeProp, onSizeChange }) => {
  const iframeRef = useRef(null);
  const [activeSection, setActiveSection] = useState("hero");
  const [activePage, setActivePage] = useState("index");  // welche Sub-Page wird aktuell im Iframe gezeigt
  const [collapsed, setCollapsed] = useState(false);
  const [internalSize, setInternalSize] = useState(() => {
    try { return localStorage.getItem("ws_mini_preview_size") || "md"; } catch { return "md"; }
  });
  // Wenn Parent eine Größe vorgibt, nutzen wir die — sonst Fallback auf interne State.
  const size = sizeProp || internalSize;
  const setSizePersist = (s) => {
    if (onSizeChange) {
      onSizeChange(s);
    } else {
      setInternalSize(s);
      try { localStorage.setItem("ws_mini_preview_size", s); } catch {}
    }
  };

  // Listen for section-open events from editor (TOC click or section expand)
  useEffect(() => {
    const handler = (e) => {
      const id = e.detail?.testid;
      const field = e.detail?.field; // optional: spezifisches Feld z.B. "services.2.title"
      const explicitPage = e.detail?.page; // optional: spezifischer Subpage-Slug (für custom-pages)
      const target = SECTION_TARGETS[id];
      if (!target && !explicitPage) return;
      setActiveSection(id || activeSection);

      // Fall A: Sub-Page-Wechsel → iframe src austauschen
      const targetPage = explicitPage || target?.page;
      if (targetPage) {
        setActivePage(targetPage);
        // src wird über useMemo unten neu berechnet → iframe lädt die neue Seite
        return;
      }

      // Fall B: Anker-Scroll auf der aktuellen Seite
      const ifr = iframeRef.current;
      if (ifr && ifr.contentWindow) {
        // Wenn wir gerade auf einer Sub-Page sind, müssen wir erst auf index zurück
        if (activePage !== "index") {
          setActivePage("index");
          // Nach Reload wird der Hash gelesen → siehe URL-Konstruktion unten
          return;
        }
        const anchor = target?.anchor || "#";
        try {
          ifr.contentWindow.postMessage(
            { type: "ws-scroll-to", anchor, field: field || null },
            "*"
          );
        } catch {}
      }
    };
    window.addEventListener("editor-section-open", handler);
    return () => window.removeEventListener("editor-section-open", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage]);

  if (!previewBase) return null;

  const dims = {
    sm: "w-[280px] h-[420px]",
    md: "w-[360px] h-[540px]",
    lg: "w-[440px] h-[660px]",
  }[size];

  // Anchor an URL hängen (Backwards-Compat falls postMessage-Skript fehlt)
  const anchor = SECTION_TO_ANCHOR[activeSection] || "#";
  // Wenn aktivePage gesetzt ist, ersetzen wir den page-Param in previewBase
  const baseWithPage = (() => {
    if (!activePage || activePage === "index") return previewBase;
    // previewBase enthält ?page=xxx&t=...
    return previewBase.replace(/([?&])page=[^&]+/, `$1page=${encodeURIComponent(activePage)}`);
  })();
  const sep = baseWithPage.includes("?") ? "&" : "?";
  const src = `${baseWithPage}${sep}live=1&rk=${refreshKey}#mp${anchor !== "#" && activePage === "index" ? `_${anchor.slice(1)}` : ""}`;

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="fixed bottom-4 right-4 z-50 bg-foreground text-background px-3 py-2 rounded-full shadow-lg flex items-center gap-2 text-xs font-bold uppercase tracking-wider hover:scale-105 transition-transform"
        data-testid="mini-preview-expand"
      >
        <Eye size={13} /> Live-Vorschau
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 ${dims} bg-card border border-border rounded-xl shadow-2xl flex flex-col overflow-hidden transition-all`}
      data-testid="floating-mini-preview"
      style={{ resize: "both" }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/40 shrink-0">
        <Eye size={12} className="text-muted-foreground" />
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground flex-1 truncate">
          Live · {activeSection}
        </span>
        <div className="flex items-center gap-0.5">
          {/* Size toggle */}
          <button
            type="button"
            onClick={() => setSizePersist(size === "sm" ? "md" : size === "md" ? "lg" : "sm")}
            title={`Größe: ${size}`}
            className="w-6 h-6 flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            data-testid="mini-preview-resize"
          >
            <Maximize2 size={11} />
          </button>
          {/* Reload */}
          <button
            type="button"
            onClick={() => {
              if (iframeRef.current) iframeRef.current.src = iframeRef.current.src;
            }}
            title="Vorschau neu laden"
            className="w-6 h-6 flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            data-testid="mini-preview-reload"
          >
            <RefreshCw size={11} />
          </button>
          {/* Collapse */}
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            title="Minimieren"
            className="w-6 h-6 flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            data-testid="mini-preview-collapse"
          >
            <span className="font-bold text-base leading-none">–</span>
          </button>
          {/* Close */}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              title="Schließen"
              className="w-6 h-6 flex items-center justify-center rounded-sm text-muted-foreground hover:text-rose-600 hover:bg-rose-50 transition-colors"
              data-testid="mini-preview-close"
            >
              <X size={11} />
            </button>
          )}
        </div>
      </div>
      {/* Iframe */}
      <iframe
        ref={iframeRef}
        src={src}
        className="flex-1 w-full border-0"
        title="Live-Mini-Vorschau"
        sandbox="allow-scripts allow-same-origin"
        data-testid="mini-preview-iframe"
      />
    </div>
  );
};
